import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';

// @mui material components
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import AddIcon from "@mui/icons-material/Add";
import Avatar from "@mui/material/Avatar";
import DownloadIcon from '@mui/icons-material/Download';
import MDInput from "components/MDInput";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// RasPm Dashboard examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import dataTableData from "./data/dataTableData";
import { RequestService, baseURLFlat } from "services/RequestService";
import CompanyForm from "../add";
import Cookies from "js-cookie";
let userTypeName = Cookies.get("userTypeName");
const CompanyAdmin = "CompanyAdmin".toUpperCase();
const SuperAdmin = "SuperAdmin".toUpperCase();

const isSuperAdmin = userTypeName?.toUpperCase() === SuperAdmin;

// Define the maximum file size in bytes (25MB)
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

function CompaniesDataTable() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState(dataTableData);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "لوحة ادارة ربان السفينة",
    content: "",
    dateTime: new Date().toLocaleString('ar-IQ'),
    open: false,
  });

  const [updateDialog, setUpdateDialog] = useState({
    open: false,
    companyData: null
  });
  const [addDialog, setAddDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    url: null,
    title: "",
    loadedUrl: null
  });

  const [companyForm, setCompanyForm] = useState({
    name: "",
    email: "",
    website: "",
    description: "",
    phoneNumber: "",
    shortNumber: "",
    logoUrl: "",
    enProfileUrl: "",
    arProfileUrl: "",
    companyAdminId: ""
  });

  const [search, setSearch] = useState("");
  const [firstSearch, setFirstSearch] = useState(false);


  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalItems: 0
  });

  const closeNotification = () => setNotification({ ...notification, open: false });

  const showNotification = (message, type = "success", backendResponse = null) => {
    setNotification({
      color: type,
      icon: type === "success" ? "check" : "error",
      title: "لوحة ادارة ربان السفينة",
      content: backendResponse?.data?.message || message,
      dateTime: new Date().toLocaleString('ar-IQ'),
      open: true
    });
  };

  const handleUpdateClick = (companyData) => {
    setCompanyForm({
      name: companyData.name || "",
      email: companyData.email || "",
      website: companyData.website || "",
      description: companyData.description || "",
      phoneNumber: companyData.phoneNumber || "",
      shortNumber: companyData.shortNumber || "",
      logoUrl: companyData.logoUrl || "",
      enProfileUrl: companyData.enProfileUrl || "",
      arProfileUrl: companyData.arProfileUrl || "",
      companyAdminId: companyData.companyAdminId ? companyData.companyAdminId.toString() : ""
    });
    setUpdateDialog({ open: true, companyData });
  };

  const handleCloseUpdateDialog = () => {
    setUpdateDialog({ open: false, companyData: null });
    setCompanyForm({
      name: "",
      email: "",
      website: "",
      description: "",
      phoneNumber: "",
      shortNumber: "",
      logoUrl: "",
      enProfileUrl: "",
      arProfileUrl: "",
      companyAdminId: ""
    });
  };

  const handleUpdateSubmit = async () => {
    try {
      setLoading(true);
      const requestData = {
        id: updateDialog.companyData.id,
        name: companyForm.name,
        email: companyForm.email,
        website: companyForm.website,
        description: companyForm.description,
        phoneNumber: companyForm.phoneNumber,
        logoUrl: companyForm.logoUrl instanceof File ? companyForm.logoUrl : undefined,
        enProfileUrl: companyForm.enProfileUrl instanceof File ? companyForm.enProfileUrl : undefined,
        arProfileUrl: companyForm.arProfileUrl instanceof File ? companyForm.arProfileUrl : undefined,
      };
  
      const checkFileSize = (file, fieldName) => {
        if (file instanceof File && file.size > MAX_FILE_SIZE) {
          showNotification(`حجم الملف كبير جداً: ${fieldName}. الحد الأقصى هو 25 ميغابايت`, "error");
          setLoading(false);
          return false;
        }
        return true;
      };
  
      if (requestData.logoUrl && !checkFileSize(requestData.logoUrl, "شعار الشركة")) return;
      if (requestData.enProfileUrl && !checkFileSize(requestData.enProfileUrl, "الملف الإنجليزي")) return;
      if (requestData.arProfileUrl && !checkFileSize(requestData.arProfileUrl, "الملف العربي")) return;
  
      const response = await RequestService.putFormData(
        `Companies/${updateDialog.companyData.id}`,
        requestData
      );
  
      if (response.data && !response.data.error) {
        showNotification("تم تحديث الشركة بنجاح", "success");
        handleCloseUpdateDialog();
        fetchCompanies();
      } else {
        showNotification(response.data?.message || "حدث خطأ أثناء التحديث", "error");
      }
    } catch (error) {
      console.error("Error:", error);
      showNotification(
        error.response?.data?.message || "فشل الاتصال بالخادم",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setCompanyForm({
      name: "",
      email: "",
      website: "",
      description: "",
      phoneNumber: "",
      shortNumber: "",
      logoUrl: "",
      enProfileUrl: "",
      arProfileUrl: "",
      companyAdminId: ""
    });
    setAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialog(false);
    setCompanyForm({
      name: "",
      email: "",
      website: "",
      description: "",
      phoneNumber: "",
      shortNumber: "",
      logoUrl: "",
      enProfileUrl: "",
      arProfileUrl: "",
      companyAdminId: ""
    });
  };


  const handleAddSubmit = async () => {
    if (companyForm.name && companyForm.email && companyForm.website) {
      try {
        setLoading(true);
        const requestData = {
          Name: companyForm.name,
          Email: companyForm.email,
          Website: companyForm.website,
          Description: companyForm.description,
          PhoneNumber: companyForm.phoneNumber,
          ShortNumber: companyForm.shortNumber,
          LogoUrl: companyForm.logoUrl instanceof File ? companyForm.logoUrl : undefined,
          EnProfileUrl: companyForm.enProfileUrl instanceof File ? companyForm.enProfileUrl : undefined,
          ArProfileUrl: companyForm.arProfileUrl instanceof File ? companyForm.arProfileUrl : undefined,
        };
  
        const checkFileSize = (file, fieldName) => {
          if (file instanceof File && file.size > MAX_FILE_SIZE) {
            showNotification(`حجم الملف كبير جداً: ${fieldName}. الحد الأقصى هو 25 ميغابايت`, "error");
            setLoading(false);
            return false;
          }
          return true;
        };
  
        if (requestData.LogoUrl && !checkFileSize(requestData.LogoUrl, "شعار الشركة")) return;
        if (requestData.EnProfileUrl && !checkFileSize(requestData.EnProfileUrl, "الملف الإنجليزي")) return;
        if (requestData.ArProfileUrl && !checkFileSize(requestData.ArProfileUrl, "الملف العربي")) return;
  
        // إرسال البيانات باستخدام postFormData
        const response = await RequestService.postFormData(
          'Companies/CreateCompany',
          requestData
        );
  
        if (response.data && !response.data.error) {
          showNotification("تمت إضافة الشركة بنجاح", "success");
          handleCloseAddDialog();
          fetchCompanies();
        } else {
          showNotification(response.data?.message || "حدث خطأ أثناء الإضافة", "error");
        }
      } catch (error) {
        console.error("Error:", error);
        showNotification(
          error.response?.data?.message || "فشل الاتصال بالخادم",
          "error"
        );
      } finally {
        setLoading(false);
      }
    } else {
      showNotification("يرجى تعبئة الحقول المطلوبة", "error");
    }
  };

  
  const handlePreviewClick = async (url, title) => {
    try {
      const loadedUrl = await RequestService.loadImage(`${baseURLFlat}${url}`);
      setPreviewDialog({
        open: true,
        url,
        title,
        loadedUrl
      });
    } catch (error) {
      console.error('Error loading image:', error);
      showNotification("حدث خطأ أثناء تحميل الصورة", "error");
    }
  };

  const handleClosePreview = () => {
    if (previewDialog.loadedUrl) {
      URL.revokeObjectURL(previewDialog.loadedUrl);
    }
    setPreviewDialog({
      open: false,
      url: null,
      title: "",
      loadedUrl: null
    });
  };

  const handleDownloadProfile = async (url, fileName) => {
    try {
      const blob = await RequestService.downloadFile(url);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      showNotification("تم تحميل الملف بنجاح", "success");
    } catch (error) {
      console.error('Error downloading profile:', error);
      showNotification("حدث خطأ أثناء تحميل الملف", "error");
    }
  };

  const handleViewPDF = async (url) => {
    try {
      const blob = await RequestService.downloadFile(url);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      // Extract filename from URL or use a default name
      const fileName = url.split('/').pop() || 'company-profile.pdf';
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      showNotification("تم تحميل الملف بنجاح", "success");
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showNotification("حدث خطأ أثناء تحميل الملف", "error");
    }
  };

  const CompanyLogo = ({ logoUrl, name, onClick }) => {
    const [loadedImageUrl, setLoadedImageUrl] = useState(null);

    useEffect(() => {
      const loadLogo = async () => {
        if (logoUrl) {
          try {
            const imageUrl = await RequestService.loadImage(`${baseURLFlat}${logoUrl}`);
            setLoadedImageUrl(imageUrl);
          } catch (error) {
            console.error('Error loading logo:', error);
          }
        }
      };

      loadLogo();

      return () => {
        if (loadedImageUrl) {
          URL.revokeObjectURL(loadedImageUrl);
        }
      };
    }, [logoUrl]);

    return (
      <Avatar
        src={loadedImageUrl || ""}
        alt={name}
        sx={{ width: 40, height: 40, cursor: 'pointer' }}
        onClick={onClick}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "";
        }}
      />
    );
  };

  CompanyLogo.propTypes = {
    logoUrl: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired
  };

  const transformCompanyData = (companies) => {
    return companies.map((company, index) => ({
      id: company.id,
      logo: (
        <MDBox display="flex" alignItems="center">
          {company.logoUrl && (
            <CompanyLogo
              logoUrl={company.logoUrl}
              name={company.name}
              onClick={() => handlePreviewClick(company.logoUrl, "شعار الشركة")}
            />
          )}
        </MDBox>
      ),
      name: company.name || "—",
      email: company.email || "—",
      website: company.website || "—",
      phoneNumber: company.phoneNumber || "—",
      shortNumber: company.shortNumber || "—",
      actions: (
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            onClick={() => handleViewPDF(company.enProfileUrl)}
            startIcon={<DownloadIcon />}
            disabled={!company.enProfileUrl}
          >
            Download English Profile
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            onClick={() => handleViewPDF(company.arProfileUrl)}
            startIcon={<DownloadIcon />}
            disabled={!company.arProfileUrl}
          >
            تحميل الملف التعريفي
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => handleUpdateClick(company)}
            startIcon={<Icon>edit</Icon>}
          >
            تعديل
          </MDButton>
        </MDBox>
      ),
    }));
  };

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const skip = (pagination.page - 1) * pagination.pageSize;
      const take = pagination.pageSize;

      const response = await RequestService.get(`Companies?searchTerm=${search}&skip=${skip}&take=${take}`);
      if (response.data && !response.data.error) {
        setTableData({
          ...tableData,
          rows: transformCompanyData(response.data.data)
        });
        setPagination({
          ...pagination,
          totalItems: response.data.totalCount // تأكد من أن API يعيد totalCount
        });
      } else {
        showNotification("حدث خطأ أثناء جلب الشركات", "error", response);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء جلب الشركات", "error", error.response);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handlePageSizeChange = (newSize) => {
    setPagination({ page: 1, pageSize: newSize, totalItems: pagination.totalItems });
  };

  useEffect(() => {
    fetchCompanies();
  }, [pagination.page, pagination.pageSize]);


  useEffect(() => {
    if (search || firstSearch) {
      setFirstSearch(true);
      setPagination(prev => ({ ...prev, page: 1 }));
      const timer = setTimeout(() => {
        fetchCompanies();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [search]);

  const renderDialog = (title, isOpen, onClose, onSubmit) => (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      dir="rtl"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <CompanyForm
          formData={companyForm}
          handleChange={setCompanyForm}
          showNotification={showNotification}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', p: 2, gap: 1 }}>
        <MDButton onClick={onSubmit} variant="gradient" color="info" size="small">
          {title === "إضافة شركة جديدة" ? "إضافة" : "حفظ التغييرات"}
        </MDButton>
        <MDButton onClick={onClose} variant="outlined" color="error" size="small">
          إلغاء
        </MDButton>
      </DialogActions>
    </Dialog>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
                sx={{
                  backgroundColor: "grey.100",
                  borderBottom: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    قائمة الشركات
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    إدارة الشركات وبياناتها
                  </MDTypography>
                </MDBox>
                <MDBox width="14rem" ml="auto">
                  <MDInput
                    placeholder="بحث ..."
                    value={search}
                    fullWidth
                    onChange={({ currentTarget }) => {
                      setSearch(currentTarget.value);
                    }}
                    sx={{ margin: "0 -10px" }}
                  />
                </MDBox>
                {isSuperAdmin && (
                  <MDButton
                    variant="gradient"
                    color="info"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                  >
                    إضافة شركة
                  </MDButton>
                )}
              </MDBox>
              <MDBox p={3}>
                <DataTable
                  table={tableData}
                  isSorted={true}
                  entriesPerPage={{
                    defaultValue: pagination.pageSize,
                    entries: [5, 10, 15, 20, 25],
                    onChange: handlePageSizeChange
                  }}
                  showTotalEntries={true}
                  noEndBorder
                  loading={loading}
                  pagination={{
                    variant: "gradient",
                    color: "info",
                    page: pagination.page,
                    totalPages: Math.ceil(pagination.totalItems / pagination.pageSize),
                    totalItems: pagination.totalItems,
                    onPageChange: handlePageChange
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Add Company Dialog */}
      {renderDialog("إضافة شركة جديدة", addDialog, handleCloseAddDialog, handleAddSubmit)}

      {/* Update Company Dialog */}
      {renderDialog("تحديث بيانات الشركة", updateDialog.open, handleCloseUpdateDialog, handleUpdateSubmit)}

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{previewDialog.title}</DialogTitle>
        <DialogContent>
          <MDBox
            component="img"
            src={previewDialog.loadedUrl}
            alt={previewDialog.title}
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: '70vh',
              objectFit: 'contain'
            }}
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClosePreview} color="info">
            إغلاق
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        dateTime={notification.dateTime}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
        dir="rtl"
      />
      <Footer />
    </DashboardLayout>
  );
}

export default CompaniesDataTable;